import React, { useEffect, useId, useRef, useState } from 'react';
import { Clock, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { ThemeColors } from '../../contexts/ThemeContext';

type HintColors = Pick<ThemeColors, 'border' | 'surface' | 'text' | 'textSecondary' | 'textMuted' | 'interactiveHover'>;

export interface ListRefreshHintProps {
  colors: HintColors;
  isRefreshing: boolean;
  lastRefreshedAt: Date | null;
  onRefresh: () => void | Promise<void>;
}

/**
 * Toolbar refresh control with a rich hover/focus hint (icons + copy), aligned with e.g. BookmarksTopbar tooltips.
 */
export const ListRefreshHint: React.FC<ListRefreshHintProps> = ({
  colors,
  isRefreshing,
  lastRefreshedAt,
  onRefresh,
}) => {
  const hintId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [hintVisible, setHintVisible] = useState(false);
  const [hintPos, setHintPos] = useState<{ top: number; left: number } | null>(null);
  const lastLabel = lastRefreshedAt
    ? lastRefreshedAt.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' })
    : null;
  const fallbackHint = isRefreshing
    ? 'Refreshing data…'
    : lastLabel
      ? `Refresh list from server. Last refreshed: ${lastLabel}`
      : 'Refresh list from server';

  const updateHintPos = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHintPos({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  };

  useEffect(() => {
    if (!hintVisible) return;
    updateHintPos();
    const onReposition = () => updateHintPos();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [hintVisible]);

  return (
    <div className="relative inline-flex flex-shrink-0">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => void onRefresh()}
        onMouseEnter={() => {
          updateHintPos();
          setHintVisible(true);
        }}
        onMouseLeave={() => setHintVisible(false)}
        onFocus={() => {
          updateHintPos();
          setHintVisible(true);
        }}
        onBlur={() => setHintVisible(false)}
        disabled={isRefreshing}
        aria-describedby={hintId}
        aria-label={fallbackHint}
        className={cn(
          'inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors',
          `${colors.surface} ${colors.text} ${colors.border} ${colors.interactiveHover}`,
          isRefreshing && 'opacity-70 cursor-wait',
        )}
      >
        {isRefreshing ? (
          <Loader2 className="h-5 w-5 animate-spin text-inherit" aria-hidden />
        ) : (
          <RefreshCw className="h-5 w-5 text-inherit" aria-hidden />
        )}
      </button>

      <div
        id={hintId}
        role="tooltip"
        aria-hidden={!hintVisible}
        className={cn(
          'pointer-events-none fixed z-[120] w-max max-w-[min(20rem,calc(100vw-2rem))] -translate-x-1/2',
          'rounded-lg border px-3 py-2.5 shadow-lg',
          'invisible opacity-0 transition-[opacity,visibility] duration-150',
          hintVisible && 'visible opacity-100',
          colors.surface,
          colors.border,
        )}
        style={hintPos ? { top: hintPos.top, left: hintPos.left } : undefined}
      >
        <div className="flex items-start gap-2">
          <RefreshCw className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', colors.textMuted)} aria-hidden />
          <p className={cn('text-xs font-semibold leading-snug', colors.text)}>Reload from server</p>
        </div>
        <p className={cn('mt-1 pl-[1.375rem] text-[0.6875rem] leading-snug', colors.textSecondary)}>
          Fetches the latest rows for this list (same filters, sort, and page).
        </p>
        <div className={cn('mt-2 flex items-start gap-2 border-t pt-2', colors.border)}>
          <Clock className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', colors.textMuted)} aria-hidden />
          <p className={cn('text-xs leading-snug', colors.textSecondary)}>
            {isRefreshing ? (
              <span className="inline-flex items-center gap-1.5 font-medium">
                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
                Refreshing…
              </span>
            ) : lastLabel ? (
              <>
                <span className={cn('font-medium', colors.text)}>Last refreshed</span>
                <span className="mt-0.5 block tabular-nums">{lastLabel}</span>
              </>
            ) : (
              <span>Not refreshed manually yet — click the arrow control when you need the newest data.</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};
